import { buildFrame } from '@/store/frame';
import { assert } from '@/utils/error';
import { Globe } from "@hscmap/stellar-globe";
import { Component, Vue, Watch } from 'vue-property-decorator';
import { config } from "./config";
import { moduleRegistry, ModuleFrameStore, ModuleNames, ModuleRegistry } from '@/modules';
import { queryParams } from '@/utils/query_params';

@Component
export class Store extends Vue {
  cursor: string | null = null
  frames: Frame[] = []
  currentFrameIndex = 0

  get currentFrame() {
    return this.frames[this.currentFrameIndex]
  }

  config = config.build()
  fullscreen = false

  private buildFrame() {
    return {
      ...buildFrame(this),
      modules: this.buildFrameModules(),
    }
  }

  private buildFrameModules() {
    const modules: ModuleFrameStore = {} as any
    for (const modname of Object.keys(moduleRegistry)) {
      modules[modname as ModuleNames] = this.modules[modname as ModuleNames].frameStore()
    }
    return modules
  }

  addNewFrame() {
    this.frames.push(this.buildFrame())
  }

  // non-reactive members

  modules!: { [P in ModuleNames]: InstanceType<ModuleRegistry[P]> }
  globes!: Map<Frame, Globe>

  beforeCreate() {
    this.globes = new Map()
    this.modules = (function buildModules() {
      const modules: { [P in ModuleNames]: InstanceType<ModuleRegistry[P]> } = {} as any
      for (const modname of Object.keys(moduleRegistry)) {
        // @ts-ignore
        modules[modname as ModuleNames] = new moduleRegistry[modname as ModuleNames]()
      }
      return modules
    })()
  }

  created() {
    this.frames.push(this.buildFrame())
    this.loadStateFromLocalStorage()
    if (queryParams.jupyter) {
      this.jupyterFrame = this.buildFrame()
    }
  }

  globe(frame: Frame) {
    return this.globes.get(frame)!
  }

  currentGlobe() {
    return this.globe(this.currentFrame)
  }

  globeRegister(frame: Frame, globe: Globe) {
    this.globes.set(frame, globe)
  }

  globeUnregister(frame: Frame) {
    // console.assert(this.globes.has(frame))
    // this.globes.delete(frame)
  }

  // persistent
  get persistent() {
    const modules: { [P in ModuleNames]: ReturnType<InstanceType<ModuleRegistry[P]>["frameStore"]> } = {} as any
    for (const modname of Object.keys(moduleRegistry)) {
      const mod = (this.modules[modname as ModuleNames] as any).persistent
      mod && (modules[modname as ModuleNames] = mod)
    }
    return {
      config: this.config,
      modules,
    }
  }

  set persistent(value: any) {
    // tslint:disable-next-line:no-shadowed-variable
    const { config, modules } = value
    this.config = config
    for (const modname of Object.keys(moduleRegistry)) {
      const c = modules[modname];
      (this.modules[modname as ModuleNames] as any).persistent = c
    }
  }

  @Watch('persistent', { deep: true })
  saveStateToLocalStorage() {
    localStorage.setItem(LOCAL_STORAGE_KEY, JSON.stringify(this.persistent))
  }

  loadStateFromLocalStorage() {
    const json = localStorage.getItem(LOCAL_STORAGE_KEY)
    if (json) {
      try {
        const value = JSON.parse(json)
        deepMerge(this.persistent, value)
      } catch {
        localStorage.removeItem(LOCAL_STORAGE_KEY)
      }
    }
  }

  reset() {
    localStorage.removeItem(LOCAL_STORAGE_KEY)
    location.href = './'
    location.reload()
  }

  progresses: Array<{ label: string }> = []

  async asyncProgress(label: string, cb: () => Promise<any>) {
    const progress = { label }
    this.progresses.push(progress)
    try {
      await cb()
    } finally {
      this.progresses.splice(this.progresses.indexOf(progress), 1)
    }
  }

  jupyterFrame: null | Frame = null
}

function isDictionary(a: any) {
  return typeof a === 'object' && !Array.isArray(a)
}

function deepMerge(a: any, b: any) {
  assert(isDictionary(a))
  assert(isDictionary(b))
  for (const bk in b) {
    if (isDictionary(b[bk])) {
      deepMerge(a[bk], b[bk])
    } else {
      a[bk] = b[bk]
    }
  }
}

const LOCAL_STORAGE_KEY =
  process.env.NODE_ENV === 'development' ? `@hscmap/hscmap/5/${process.env.VUE_APP_DATASET}` : '@hscmap/hscmap/5'
export type Frame = ReturnType<InstanceType<typeof Store>["buildFrame"]>

const store = new Store()
export { store };
